import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, handleAuthSuccess } from '../services/api';
import GoogleLoginButton from '../components/GoogleLoginButton';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Brain } from 'lucide-react';
const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  // Update the initial form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '', // Add dateOfBirth field
    password: '',
    confirmPassword: '',
    timezone: 'UTC-8',
    agreedToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Color constants matching Header component
  const marineBlue = '#1e5f74';
  const lightBlue = '#5fb3d4';
  // ✅ Handles form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (error) setError('');
    // Clear field-specific error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle successful registration
  const handleRegistrationSuccess = (response) => {
    // Store user info for checkout
    const userData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      dateOfBirth: formData.dateOfBirth,
      id: response.data?.user?.id || response.user?.id,
      role: 'patient', // Explicitly set role as patient
      profileImage: response.data?.user?.profileImage || response.user?.profileImage
    };

    // Store user data in localStorage
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('checkoutUserInfo', JSON.stringify(userData));

    // Store auth token
    if (response.data?.token || response.token) {
      const token = response.data?.token || response.token;
      localStorage.setItem('access_token', token);
      localStorage.setItem('token_timestamp', Date.now().toString());
    }

    // Navigate to care plan pricing section if redirectTo is set
    if (redirectTo && redirectTo.includes('/care-plan')) {
      navigate('/care-plan#pricing');
    } else {
      // Otherwise use default navigation
      handleAuthSuccess(response, navigate);
    }
  };

  // ✅ Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFormErrors({});

    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (!nameRegex.test(formData.name)) {
      errors.name = 'Name can only contain letters and spaces';
    }

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreedToTerms) {
      errors.agreedToTerms = 'You must agree to the terms';
    }

    // Simple date of birth validation
    if (!formData.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth, // Add dateOfBirth to API request
        timezone: formData.timezone,
        role: 'patient' // Always set role as patient for registration
      };

      console.log('Registering with data:', userData);
      const response = await authAPI.register(userData);
      console.log('Registration response:', response);

      handleRegistrationSuccess(response);
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response?.data?.message?.toLowerCase()?.includes('email')) {
        setFormErrors({ email: 'Email already exists' });
      } else {
        setError(error.response?.data?.message || error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (result) => {
    console.log('Google registration successful:', result);
    setError('');
  };

  const handleGoogleError = (error) => {
    console.error('Google registration error:', error);
    setError(error.message || 'Google registration failed. Please try again.');
  };

  return (
    <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
      {/* LEFT SECTION */}
      <div className="w-full lg:w-1/2 bg-blue-600 flex justify-end py-8">
        {/* Registration Card */}
        <div className="max-w-7xl sm:max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]">
          <div className="px-4 sm:px-6 lg:px-8">
             {/* Company Logo */}
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Brain className="h-8 w-8" style={{color: lightBlue}} />
            <span className="text-2xl font-bold">
              <span style={{color: lightBlue}}>ADHD</span>
              <span style={{color: marineBlue}}>RapidCare</span>
            </span>
          </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">Create Your Account</h2>
            <p className="text-gray-600 text-sm mb-6 text-center">Start your ADHD assessment journey online</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter name"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="john@email.com"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  required
                  max={new Date().toISOString().split('T')[0]}
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {formErrors.dateOfBirth && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.dateOfBirth}</p>
                )}
              </div>

              {/* Create Password */}
              <div className="relative">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Create password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Your password"
                />
                <div
                  className="absolute top-9 right-3 cursor-pointer text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </div>
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    formErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Confirm password"
                />
                <div
                  className="absolute top-9 right-3 cursor-pointer text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </div>
                {formErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>
                )}
              </div>

              {/* Timezone */}
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Your timezone
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="UTC-8">UTC-8 (Pacific Time)</option>
                  <option value="UTC-7">UTC-7 (Mountain Time)</option>
                  <option value="UTC-6">UTC-6 (Central Time)</option>
                  <option value="UTC-5">UTC-5 (Eastern Time)</option>
                  <option value="UTC-4">UTC-4 (Atlantic Time)</option>
                  <option value="UTC-3">UTC-3 (Brazil Time)</option>
                  <option value="UTC+0">UTC+0 (GMT)</option>
                  <option value="UTC+1">UTC+1 (Central European Time)</option>
                  <option value="UTC+2">UTC+2 (Eastern European Time)</option>
                  <option value="UTC+5:30">UTC+5:30 (India Standard Time)</option>
                  <option value="UTC+8">UTC+8 (China Standard Time)</option>
                  <option value="UTC+9">UTC+9 (Japan Standard Time)</option>
                </select>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start">
                <input
                  id="agreedToTerms"
                  name="agreedToTerms"
                  type="checkbox"
                  required
                  checked={formData.agreedToTerms}
                  onChange={handleChange}
                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border rounded mt-1 ${
                    formErrors.agreedToTerms ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <label htmlFor="agreedToTerms" className="ml-2 block text-sm text-gray-900">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 hover:text-blue-500">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-500">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {formErrors.agreedToTerms && (
                <p className="text-red-500 text-xs mt-1">{formErrors.agreedToTerms}</p>
              )}

              {/* Create Account Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    'Create My Account'
                  )}
                </button>
              </div>

              {/* Google Sign Up */}
              {/* <div>
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  disabled={loading}
                  text="Sign up with Google"
                  theme="outline"
                  size="large"
                  className="w-full"
                />
              </div> */}

              {/* Login Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                    Log In
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
        <div className="bg-blue-600 text-white shadow-xl p-9 max-w-7xl sm:max-w-xl w-full min-h-[600px]">
          <div className="flex flex-col justify-center h-full">
            {/* Profile Image */}
            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
              <img
                src="/images/signup2.jfif"
                alt="Doctor"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Heading */}
            <h2 className="text-2xl font-bold text-center mb-3">Welcome to ADHDRapidCare</h2>
            <p className="text-base text-white/90 text-center mb-8">
              Your journey to better mental health starts here
            </p>

            {/* Feature List */}
            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
              {/* Feature 1 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                  <img
                    src="/images/SVG1.png"  
                    alt="Brain icon"
                    className="w-5 h-5 object-contain"
                  />
                </div>

                <div>
                  <p className="text-base font-semibold">AI-Powered Diagnosis</p>
                  <p className="text-sm text-white/80">Advanced technology meets clinical expertise</p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">                
                  <img
                    src="/images/SVG 2.png"  
                    alt="Brain icon"
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div>
                  <p className="text-base font-semibold">Licensed Medical Professionals</p>
                  <p className="text-sm text-white/80">Care from certified healthcare providers</p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center"> 
                  <img
                    src="/images/SVG 3.png"
                    alt="Brain icon"
                    className="w-5 h-5 object-contain"
                  />
                </div>
                <div>
                  <p className="text-base font-semibold">Personalised Care Plans</p>
                  <p className="text-sm text-white/80">Tailored treatment for your unique needs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
